import { supabase } from '@/integrations/supabase/client';
import { Message, Conversation, ChatPartner } from '@/types/chat';
import { getSignedMediaUrl } from '@/utils/signedMediaUtils';
import { debugLogger } from '@/utils/debugLogger';
import { AdminService } from '@/core/services/AdminService';
import { blockService } from '@/core/services/BlockService';

export interface ChatListData {
  conversations: any[];
  totalCount: number;
}

export class ChatDataService {
  private static instance: ChatDataService;
  
  static getInstance(): ChatDataService {
    if (!ChatDataService.instance) {
      ChatDataService.instance = new ChatDataService();
    }
    return ChatDataService.instance;
  }

  async getChatList(userId: string): Promise<ChatListData> {
    try {
      debugLogger.debug('[ChatDataService] Fetching conversations for user:', userId);
      
      // Get all blocked user IDs (both directions)
      const blockedUserIds = await blockService.getAllBlockedUserIds(userId);
      debugLogger.debug('[ChatDataService] Blocked user IDs:', blockedUserIds);
      
      // Use simpler query approach to avoid 406 errors and filter deleted conversations
      const { data: conversations, error } = await supabase
        .from('conversations')
        .select(`
          id,
          created_at,
          updated_at,
          last_message_text,
          last_message_time,
          unread_count_user1,
          unread_count_user2,
          user1_id,
          user2_id,
          deleted_at,
          deleted_by,
          user1_profile:profiles!conversations_user1_id_fkey(
            id,
            username,
            main_photo_url,
            hidden,
            onboarding_completed
          ),
          user2_profile:profiles!conversations_user2_id_fkey(
            id,
            username,
            main_photo_url,
            hidden,
            onboarding_completed
          )
        `)
        .or(`user1_id.eq.${userId},user2_id.eq.${userId}`)
        .is('deleted_at', null)
        .order('last_message_time', { ascending: false, nullsFirst: false });

      if (error) {
        debugLogger.error('[ChatDataService] Error fetching conversations:', error);
        throw error;
      }

      debugLogger.debug('[ChatDataService] Raw conversations data:', conversations);

      // Process conversations and filter out blocked users
      const processedConversations = conversations?.map(conv => {
        // Determine which user is the partner (not the current user)
        const partnerId = conv.user1_id === userId ? conv.user2_id : conv.user1_id;
        const partnerProfile = conv.user1_id === userId ? conv.user2_profile : conv.user1_profile;
        
        debugLogger.debug('[ChatDataService] Processing conversation:', {
          convId: conv.id,
          currentUserId: userId,
          user1Id: conv.user1_id,
          user2Id: conv.user2_id,
          partnerId,
          partnerProfile,
          user1Profile: conv.user1_profile,
          user2Profile: conv.user2_profile
        });

        return {
          ...conv,
          partnerId, // Add partnerId for filtering
          partner: {
            id: partnerId,
            username: partnerProfile?.username || 'Usuário',
            main_photo_url: partnerProfile?.main_photo_url || '',
            hidden: partnerProfile?.hidden || false,
            onboarding_completed: partnerProfile?.onboarding_completed || false
          }
        };
      }).filter(conv => {
        // Filter out conversations with blocked users
        const isBlocked = blockedUserIds.includes(conv.partnerId);
        if (isBlocked) {
          debugLogger.debug('[ChatDataService] Filtering out blocked conversation with partner:', conv.partnerId);
        }
        return !isBlocked;
      }) || [];

      debugLogger.debug('[ChatDataService] Processed conversations (after block filter):', processedConversations.length);

      return {
        conversations: processedConversations,
        totalCount: processedConversations.length
      };
    } catch (error) {
      debugLogger.error('[ChatDataService] Error fetching chat list:', error);
      return {
        conversations: [],
        totalCount: 0
      };
    }
  }

  async deleteConversation(conversationId: string, userId: string): Promise<boolean> {
    try {
      debugLogger.debug('[ChatDataService] Starting conversation deletion:', { conversationId, userId });
      
      // Verify user is part of the conversation before allowing deletion
      const { data: conversation, error: fetchError } = await supabase
        .from('conversations')
        .select('user1_id, user2_id')
        .eq('id', conversationId)
        .is('deleted_at', null)
        .single();

      if (fetchError || !conversation) {
        debugLogger.error('[ChatDataService] Conversation not found or already deleted:', fetchError);
        return false;
      }

      // Check if user is part of the conversation
      if (conversation.user1_id !== userId && conversation.user2_id !== userId) {
        debugLogger.error('[ChatDataService] User not authorized to delete this conversation');
        return false;
      }

      // Perform soft delete with explicit timestamp and deleted_by field
      const deleteTimestamp = new Date().toISOString();
      const { error: deleteError } = await supabase
        .from('conversations')
        .update({ 
          deleted_at: deleteTimestamp,
          deleted_by: userId, // NOVO: Registrar quem deletou a conversa
          updated_at: deleteTimestamp
        })
        .eq('id', conversationId);

      if (deleteError) {
        debugLogger.error('[ChatDataService] Error deleting conversation:', deleteError);
        return false;
      }

      debugLogger.debug('[ChatDataService] Conversation successfully soft-deleted:', { 
        conversationId, 
        deletedAt: deleteTimestamp,
        deletedBy: userId
      });
      
      // Add a small delay to ensure database has processed the change
      await new Promise(resolve => setTimeout(resolve, 100));
      
      return true;
    } catch (error) {
      debugLogger.error('[ChatDataService] Error in deleteConversation:', error);
      return false;
    }
  }

  async restoreConversation(conversationId: string, userId: string): Promise<boolean> {
    try {
      debugLogger.debug('[ChatDataService] Starting conversation restoration:', { conversationId, userId });
      
      // Verify user is part of the conversation and that they deleted it
      const { data: conversation, error: fetchError } = await supabase
        .from('conversations')
        .select('user1_id, user2_id, deleted_by')
        .eq('id', conversationId)
        .not('deleted_at', 'is', null)
        .single();

      if (fetchError || !conversation) {
        debugLogger.error('[ChatDataService] Conversation not found or not deleted:', fetchError);
        return false;
      }

      // Check if user is part of the conversation
      if (conversation.user1_id !== userId && conversation.user2_id !== userId) {
        debugLogger.error('[ChatDataService] User not authorized to restore this conversation');
        return false;
      }

      // Verificar se o usuário é administrador
      const isAdmin = await AdminService.isAdmin(userId);

      // Check if the user is the one who deleted it OR is an admin
      if (conversation.deleted_by !== userId && !isAdmin) {
        debugLogger.error('[ChatDataService] Only the user who deleted or admins can restore the conversation');
        return false;
      }

      // Log quando admin restaura conversa deletada por outro usuário
      if (isAdmin && conversation.deleted_by !== userId) {
        debugLogger.info('[ChatDataService] Admin restoring conversation deleted by another user:', {
          conversationId,
          adminId: userId,
          deletedBy: conversation.deleted_by
        });
      }

      // Restore conversation by clearing soft delete fields
      const { error: restoreError } = await supabase
        .from('conversations')
        .update({ 
          deleted_at: null,
          deleted_by: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', conversationId);

      if (restoreError) {
        debugLogger.error('[ChatDataService] Error restoring conversation:', restoreError);
        return false;
      }

      debugLogger.debug('[ChatDataService] Conversation successfully restored:', conversationId);
      
      // Add a small delay to ensure database has processed the change
      await new Promise(resolve => setTimeout(resolve, 100));
      
      return true;
    } catch (error) {
      debugLogger.error('[ChatDataService] Error in restoreConversation:', error);
      return false;
    }
  }

  async getConversationByUsername(currentUserId: string, username: string): Promise<string | null> {
    try {
      debugLogger.debug('[ChatDataService] Finding conversation for user:', username);
      
      // Find the user by username
      const { data: targetUser, error: userError } = await supabase
        .from('profiles')
        .select('id')
        .eq('username', username)
        .maybeSingle();

      if (userError) {
        debugLogger.error('[ChatDataService] Error finding user:', userError);
        return null;
      }

      if (!targetUser) {
        debugLogger.error('[ChatDataService] User not found:', username);
        return null;
      }

      debugLogger.debug('[ChatDataService] Found target user:', targetUser.id);

      // Check if conversation exists using separate queries to avoid 406
      let existingConv = null;
      
      // Try first combination
      const { data: conv1 } = await supabase
        .from('conversations')
        .select('id')
        .eq('user1_id', currentUserId)
        .eq('user2_id', targetUser.id)
        .is('deleted_at', null)
        .maybeSingle();

      if (conv1) {
        existingConv = conv1;
      } else {
        // Try reverse combination
        const { data: conv2 } = await supabase
          .from('conversations')
          .select('id')
          .eq('user1_id', targetUser.id)
          .eq('user2_id', currentUserId)
          .is('deleted_at', null)
          .maybeSingle();
        
        existingConv = conv2;
      }

      if (existingConv) {
        debugLogger.debug('[ChatDataService] Found existing conversation:', existingConv.id);
        return existingConv.id;
      }

      // Create new conversation
      debugLogger.debug('[ChatDataService] Creating new conversation');
      const { data: newConv, error: createError } = await supabase
        .from('conversations')
        .insert({
          user1_id: currentUserId,
          user2_id: targetUser.id
        })
        .select('id')
        .single();

      if (createError) {
        debugLogger.error('[ChatDataService] Error creating conversation:', createError);
        return null;
      }

      debugLogger.debug('[ChatDataService] Created new conversation:', newConv.id);
      return newConv.id;
    } catch (error) {
      debugLogger.error('[ChatDataService] Error in getConversationByUsername:', error);
      return null;
    }
  }

  async getPartnerDetails(conversationId: string, currentUserId: string): Promise<ChatPartner | null> {
    try {
      // Get conversation to identify partner
      const { data: conversation, error: convoError } = await supabase
        .from('conversations')
        .select('user1_id, user2_id')
        .eq('id', conversationId)
        .is('deleted_at', null)
        .single();

      if (convoError || !conversation) {
        debugLogger.error('Error fetching conversation:', convoError);
        return null;
      }

      // Determine partner ID
      const partnerId = conversation.user1_id === currentUserId 
        ? conversation.user2_id 
        : conversation.user1_id;

      // Fetch partner profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id, username, main_photo_url')
        .eq('id', partnerId)
        .single();

      if (profileError) {
        debugLogger.error('Error fetching partner profile:', profileError);
        return null;
      }

      return {
        id: profile.id,
        name: profile.username || 'Usuário',
        username: profile.username,
        avatar: profile.main_photo_url || '',
      };
    } catch (error) {
      debugLogger.error('Error in getPartnerDetails:', error);
      return null;
    }
  }

  async getMessages(conversationId: string, page = 0, limit = 20): Promise<{ messages: Message[], hasMore: boolean }> {
    try {
      // Get total count first
      const { count } = await supabase
        .from('messages')
        .select('id', { count: 'exact' })
        .eq('conversation_id', conversationId);

      const totalCount = count || 0;
      const offset = Math.max(0, totalCount - (page + 1) * limit);
      const actualLimit = Math.min(limit, totalCount - page * limit);

      if (actualLimit <= 0) {
        return { messages: [], hasMore: false };
      }

      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true })
        .range(offset, offset + actualLimit - 1);

      if (error) throw error;

      // Convert media URLs to signed URLs
      const messagesWithSignedUrls = await Promise.all(
        (data as Message[]).map(async (message) => {
          if (message.media_url && (message.content_type === 'image' || message.content_type === 'audio')) {
            const signedUrl = await getSignedMediaUrl(message.media_url);
            return {
              ...message,
              media_url: signedUrl || message.media_url
            };
          }
          return message;
        })
      );

      const hasMore = page === 0 && data.length === limit && totalCount > limit;

      return { 
        messages: messagesWithSignedUrls, 
        hasMore 
      };
    } catch (error) {
      debugLogger.error('Error fetching messages:', error);
      return { messages: [], hasMore: false };
    }
  }

  async sendTextMessage(conversationId: string, senderId: string, content: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('messages')
        .insert([{
          conversation_id: conversationId,
          sender_id: senderId,
          content: content,
          content_type: 'text'
        }]);

      if (error) throw error;
      return true;
    } catch (error) {
      debugLogger.error('Error sending message:', error);
      return false;
    }
  }

  async sendImageMessage(conversationId: string, senderId: string, file: File): Promise<boolean> {
    try {
      // Upload file using new conversation-based structure
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `conversations/${conversationId}/${fileName}`;

      const { error: uploadError } = await supabase
        .storage
        .from('chat_media')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get signed URL instead of public URL
      const signedUrl = await getSignedMediaUrl(filePath);

      if (!signedUrl) {
        throw new Error('Failed to generate signed URL');
      }

      // Create message
      const { error: messageError } = await supabase
        .from('messages')
        .insert([{
          conversation_id: conversationId,
          sender_id: senderId,
          content: 'Imagem',
          content_type: 'image',
          media_url: filePath // Store file path, not signed URL
        }]);

      if (messageError) throw messageError;
      return true;
    } catch (error) {
      debugLogger.error('Error sending image:', error);
      return false;
    }
  }

  async sendAudioMessage(conversationId: string, senderId: string, audioBlob: Blob, duration: number): Promise<boolean> {
    try {
      // Upload audio using new conversation-based structure
      const fileName = `${Date.now()}.webm`;
      const filePath = `conversations/${conversationId}/${fileName}`;

      const { error: uploadError } = await supabase
        .storage
        .from('chat_media')
        .upload(filePath, audioBlob);

      if (uploadError) throw uploadError;

      // Get signed URL for immediate use
      const signedUrl = await getSignedMediaUrl(filePath);

      if (!signedUrl) {
        throw new Error('Failed to generate signed URL');
      }

      // Create message
      const { error: messageError } = await supabase
        .from('messages')
        .insert([{
          conversation_id: conversationId,
          sender_id: senderId,
          content: 'Áudio',
          content_type: 'audio',
          media_url: filePath, // Store file path, not signed URL
          audio_duration: duration
        }]);

      if (messageError) throw messageError;
      return true;
    } catch (error) {
      debugLogger.error('Error sending audio:', error);
      return false;
    }
  }

  async markAsRead(conversationId: string, userId: string): Promise<void> {
    try {
      // Get conversation to determine user position
      const { data: conversation, error: convoError } = await supabase
        .from('conversations')
        .select('user1_id, user2_id')
        .eq('id', conversationId)
        .is('deleted_at', null)
        .single();

      if (convoError || !conversation) return;

      // Update unread count
      const isUser1 = conversation.user1_id === userId;
      const updateField = isUser1 
        ? { unread_count_user1: 0 } 
        : { unread_count_user2: 0 };

      await supabase
        .from('conversations')
        .update(updateField)
        .eq('id', conversationId);

      // Mark messages as read
      await supabase
        .from('messages')
        .update({ read_at: new Date().toISOString() })
        .eq('conversation_id', conversationId)
        .is('read_at', null)
        .neq('sender_id', userId);
    } catch (error) {
      debugLogger.error('Error marking as read:', error);
    }
  }
}

export const chatDataService = ChatDataService.getInstance();
